import React, { useState, useEffect } from "react";
import LanguageSelector from "../LanguageSelector";
import CurrencySelector from "../CurrencySelector";
import { getUserSession, getUserFullName, clearUserSession, isUserLoggedIn, getAuthToken } from "../../utils/auth";
import { useTranslation } from "../../context/TranslationContext";
// import SignInRegisterModal from "./SignInRegisterModal";

const Header = ({
  showCheckout,
  handleBackToSummary,
  setShowSignInModal,
  showSignInModal,
  logo2000,
  navigate,
  accommodationName,
  currentLanguage,
  onLanguageChange,
  currentCurrency,
  onCurrencyChange,
  accommodationGroupDetail,
}) => {
  const { t } = useTranslation();
  const logoUrl = logo2000 || '';
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Check login status on mount and when user logs in
  useEffect(() => {
    const checkLoginStatus = () => {
      const loggedIn = isUserLoggedIn();
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        const name = getUserFullName();
        setUserName(name);
      } else {
        setUserName('');
      }
    };

    // Check on mount
    checkLoginStatus();

    // Listen for login events
    const handleUserLogin = () => {
      checkLoginStatus();
    };

    window.addEventListener('userLoggedIn', handleUserLogin);
    window.addEventListener('userLoggedOut', handleUserLogin);

    return () => {
      window.removeEventListener('userLoggedIn', handleUserLogin);
      window.removeEventListener('userLoggedOut', handleUserLogin);
    };
  }, []);

  // Handle logout
  const handleLogout = () => {
    clearUserSession();
    setIsLoggedIn(false);
    setUserName('');
    setShowUserDropdown(false);
    // Trigger logout event
    window.dispatchEvent(new CustomEvent('userLoggedOut'));
  };

  // Get LoyaltyUrl from accommodationGroupDetail and append token
  const getLoyaltyUrl = () => {
    let baseUrl = null;
    
    // Get LoyaltyUrl from accommodationGroupDetail.accommodationDetails[0].LoyaltyUrl
    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      baseUrl = accommodationGroupDetail.accommodationDetails[0]?.LoyaltyUrl || null;
    } else if (accommodationGroupDetail?.accommodationDetails?.LoyaltyUrl) {
      baseUrl = accommodationGroupDetail.accommodationDetails.LoyaltyUrl;
    }
    
    if (!baseUrl) {
      return null;
    }
    
    // Get token from user session (user->data->accessToken)
    const session = getUserSession();
    let token = null;
    
    // Priority: user->data->accessToken
    if (session?.user?.data?.directToken) {
      token = session.user.data.directToken;
    } else if (session?.user?.data?.token) {
      token = session.user.data.token;
    } else if (session?.token) {
      token = session.token;
    } else {
      // Fallback: try to get from localStorage
      token = getAuthToken();
    }
    
    if (!token) {
      return baseUrl; // Return base URL without token if token not found
    }
    
    // Construct URL: LoyaltyUrl + /dashboard?directToken=token
    // Remove trailing slash from baseUrl if exists
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    return `${cleanBaseUrl}/dashboard?token=${token}`;
  };

  // Handle My Booking click
  const handleMyBooking = () => {
    const loyaltyUrl = getLoyaltyUrl();
    if (loyaltyUrl) {
      // Open in new tab
      window.open(loyaltyUrl, '_blank');
      setShowUserDropdown(false);
    } else {
      alert('My Booking URL is not available');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);
  
  return (
    <header className="header shadow-lg transition-all duration-300 z-0 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Left section: Logo + Title */}
          <div className="flex items-center space-x-4">
            {logoUrl ? (
              <div className="bg-white rounded-lg p-1.5 shadow-md">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-8 md:h-10 w-auto object-contain"
                />
              </div>
            ) : null}
            {accommodationName && (
              <div className="flex flex-col">
                <h1 className="text-lg md:text-xl font-medium text-white logo-text leading-tight tracking-wide">
                  {showCheckout
                    ? t('header.checkout')
                    : accommodationName || ""}
                </h1>
                <p className="text-xs md:text-sm golden-txt font-light">
                  {showCheckout
                    ? t('header.completeYourBooking')
                    : ""}
                </p>
              </div>
            )}
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-3 md:space-x-4">
            {showCheckout && (
              <button
                onClick={handleBackToSummary}
                className="hidden sm:flex text-gray-100 hover:text-white px-3 py-1 border border-gray-400 rounded text-xs md:text-sm"
              >
                <svg
                  className="h-4 w-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                {t('header.backToBooking')}
              </button>
            )}

            {/* Language Selector - show only if ChooseLanguage is 1 */}
            {currentLanguage && onLanguageChange && Number(accommodationGroupDetail?.hotelMapping?.ChooseLanguage ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.ChooseLanguage ?? 0) === 1 && (
              <div className="hidden sm:block">
                <LanguageSelector
                  currentLanguage={currentLanguage}
                  onLanguageChange={onLanguageChange}
                  buttonClassName="flex items-center justify-center"
                />
              </div>
            )}

            {/* Currency Selector - show only if ChooseCurrency is 1 */}
            {currentCurrency && onCurrencyChange && Number(accommodationGroupDetail?.hotelMapping?.ChooseCurrency ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.ChooseCurrency ?? 0) === 1 && (
              <div className="hidden sm:block">
                <CurrencySelector
                  currentCurrency={currentCurrency}
                  onCurrencyChange={onCurrencyChange}
                  buttonClassName="flex items-center space-x-1 px-3 py-2 text-white-800 text-xs md:text-sm font-medium cursor-pointer min-w-[100px]"
                />
              </div>
            )}

            {/* Sign In / User Dropdown - Only show when IsLogin = 1 */}
            {Number(accommodationGroupDetail?.hotelMapping?.IsLogin ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsLogin ?? 1) === 1 && (!isLoggedIn ? (
              <button
                className="hidden sm:inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-white/40 text-white text-sm font-medium hover:bg-white hover:text-gray-900 hover:border-white transition-all duration-200 uppercase tracking-wider"
                onClick={() => setShowSignInModal(true)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('header.signIn')}
              </button>
            ) : (
              <div className="relative user-dropdown-container">
                <button
                  className="hidden sm:flex items-center gap-2 px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                >
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold">
                    {userName ? userName.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <span className="text-sm font-medium">{userName || t('header.user')}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-900">{userName || t('header.user')}</p>
                      <p className="text-xs text-gray-500">{t('header.loggedIn')}</p>
                    </div>
                    {(accommodationGroupDetail?.IsDashboard == 1 || accommodationGroupDetail?.isDashboard == 1 || accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsDashboard == 1) && (
                    <button
                      onClick={handleMyBooking}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {t('header.myBooking')}
                      </div>
                    </button>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('header.logout')}
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
